/**
 * useLogout — single source of truth for the logout flow.
 *
 * Used by both the AppShell header LogOut icon (always visible from any
 * authenticated screen) and the HamburgerMenu drawer logout button. Both
 * surfaces call into this hook so a backend / state-clearing change lands
 * in one place.
 *
 * Best-effort: if /auth/logout 401s or 5xxs we still clear local auth state
 * and navigate to /login. The server-side session cleanup is a nice-to-have;
 * the hard requirement is that the user no longer has a valid token in
 * client memory.
 */
import { useNavigate } from "react-router";

import { apiFetch } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";

export function useLogout(): () => Promise<void> {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  return async () => {
    try {
      await apiFetch<void>("/auth/logout", { method: "POST" });
    } catch {
      // Best-effort; even if backend rejects, we clear local state.
    }
    logout();
    navigate("/login", { replace: true });
  };
}
